import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs';
import { appendHttpsIfMissing } from '../common/utils/url';
import { Session } from '../models/session';
import { ActiveSpace, SpaceRepository } from '../state/space.repository';
import { EVENT_STATUS } from '../models/event';
import { EventWithTimingInfo } from '../sessions/session/attendance/attendance-ui-models';
import { UserService } from './user.service';
import { AnalyticsService } from './analytics.service';
import { TelemetryService } from './telemetry.service';
import { SpacesService } from './spaces.service';

/**
 * A task that needs to be executed before redirecting to the post-event URL.
 */
export interface PreRedirectTask {
  performTask: () => Promise<void>;
  id?: string;
}

/**
 * Service responsible for managing the redirection process when an event ends.
 * Handles pre-redirect tasks, URL construction, and session termination.
 */
@Injectable({
  providedIn: 'root',
})
export class EndEventRedirectManagerService {
  private readonly TELEMETRY_SOURCE = 'end-event-redirect-manager';
  /** Collection of tasks that need to be executed before redirecting */
  private readonly preRedirectTasks: PreRedirectTask[] = [];

  constructor(
    private userService: UserService,
    private analyticsService: AnalyticsService,
    private telemetryService: TelemetryService,
    private spacesService: SpacesService,
    private spaceRepo: SpaceRepository,
  ) {}

  public shouldAddToPreRedirectTasks(onGoingEvent: EventWithTimingInfo | undefined): boolean {
    return (
      (this.spaceRepo.activeSpace?.institution?.settings?.postEventAction?.enablePostEventAction &&
        onGoingEvent?.state?.status === EVENT_STATUS.COMPLETED) ??
      false
    );
  }

  /**
   * Adds a new task to be executed before redirecting to the post-event URL.
   * @param task - The task to be executed before redirection
   * @returns The ID of the added task for later removal
   */
  public addPreRedirectTask(task: PreRedirectTask): string {
    const taskId = crypto.randomUUID();
    this.preRedirectTasks.push({ ...task, id: taskId });
    return taskId;
  }

  /**
   * Removes a pre-redirect task by its ID
   * @param taskId - The ID of the task to remove
   */
  public removePreRedirectTask(taskId: string): void {
    const index = this.preRedirectTasks.findIndex((task) => task.id === taskId);
    if (index !== -1) {
      this.preRedirectTasks.splice(index, 1);
    }
  }

  /**
   * Redirects the user to the post event URL.
   * It first executes the pre-redirect tasks and then redirects the user.
   *
   * @param activeSpace - The current active space.
   * @param eventId - The current event ID.
   *
   * @returns A promise that resolves when the redirect is complete.
   */
  public async redirectToPostEventUrl(
    activeSpace: ActiveSpace | undefined,
    eventId: string | undefined,
  ): Promise<void> {
    const urlToNavigateTo = this.getPostEventActionUrl(eventId, activeSpace);
    if (!urlToNavigateTo) {
      return;
    }

    await this.executePreRedirectTasks();

    // Clear all tasks after execution
    this.preRedirectTasks.length = 0;

    await this.endSession(activeSpace?._id);

    window.onbeforeunload = null;
    location.replace(urlToNavigateTo);
  }

  /**
   * Executes all registered pre-redirect tasks sequentially.
   * @returns Promise that resolves when all tasks are complete
   */
  private async executePreRedirectTasks(): Promise<void> {
    for (const task of this.preRedirectTasks) {
      await task.performTask();
    }
  }

  /**
   * Retrieves the post-event URL from the space metadata based on user role.
   * @param space - The active space containing institution settings
   * @param isHost - Boolean indicating if the current user is a host
   * @returns The post-event URL if defined, undefined otherwise
   */
  private getPostEventUrlFromMetadata(
    space: ActiveSpace | undefined,
    isHost: boolean,
  ): string | undefined {
    const postEventAction = space?.institution?.settings?.postEventAction;

    return isHost
      ? postEventAction?.hostsPostEventActionMetadata?.url
      : postEventAction?.participantsPostEventActionMetadata?.url;
  }

  /**
   * Ends the current session for a given space.
   * @param spaceId - ID of the space to end the session for
   */
  private async endSession(spaceId: string | undefined): Promise<void> {
    if (!spaceId) {
      return;
    }

    try {
      await firstValueFrom(this.spacesService.endOngoingSession(spaceId).pipe(take(1)));
    } catch (error) {
      this.telemetryService.logObject({ error }, this.TELEMETRY_SOURCE, 'error');
    }
  }

  /**
   * Constructs the complete post-event action URL with appropriate user role.
   * @param eventId - ID of the current event
   * @param activeSpace - The current active space
   * @returns The constructed post-event URL or empty string if invalid
   */
  private getPostEventActionUrl(
    eventId: string | undefined,
    activeSpace: ActiveSpace | undefined,
  ): string {
    const isHost = Session.isOwnedByUser(activeSpace, this.userService.user?.value?.user);
    const baseUrl = this.getPostEventUrlFromMetadata(activeSpace, isHost);

    if (!baseUrl) {
      return '';
    }

    const url = appendHttpsIfMissing(baseUrl);
    return this.appendQueryParamsToUrl(url, eventId, activeSpace);
  }

  /**
   * Appends standard query parameters to the post-event URL.
   * Includes userId, eventId, spaceId, and sessionId if available.
   * @param url - Base URL to append parameters to
   * @param eventId - ID of the current event
   * @param activeSpace - The current active space
   * @returns Complete URL with query parameters or empty string if URL is invalid
   */
  private appendQueryParamsToUrl(
    url: string,
    eventId: string | undefined,
    activeSpace: ActiveSpace | undefined,
  ): string {
    try {
      const urlObject = new URL(url);

      // add the user id
      this.userService.user?.value?.user?._id &&
        urlObject.searchParams.set('userId', this.userService.user?.value?.user?._id);

      // add the event id
      eventId && urlObject.searchParams.set('eventId', eventId);

      // add the space id
      activeSpace?._id && urlObject.searchParams.set('spaceId', activeSpace._id);

      // add the session id
      const currentSessionId = this.analyticsService.getCurrentSessionId();
      currentSessionId && urlObject.searchParams.set('sessionId', currentSessionId);

      return urlObject.toString();
    } catch (error) {
      this.telemetryService.logObject(
        { error: 'Invalid URL format', url },
        this.TELEMETRY_SOURCE,
        'error',
      );
      return '';
    }
  }
}
